import React, { createContext, FC, PropsWithChildren, useCallback, useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '@modules/me';
import { CounterService } from '../services';
import { ICounter } from '../interfaces';

const initialState: ICounter = {
  couriers: 0,
  orders: 0,
  declarations: 0,
  unknownDeclarations: 0,
  supports: 0,
  handoverQueue: { pending: 0, executing: 0, executed: 0 },
  byBranch: {},
};

export type CounterContextValue = {
  state: ICounter;
  onMouseEnter: () => void;
  lastUpdatedAt: number | null;
};

export const CounterContext = createContext<CounterContextValue>({
  state: initialState,
  onMouseEnter: () => {},
  lastUpdatedAt: null,
});

export const CounterProvider: FC<PropsWithChildren> = ({ children }) => {
  const { state: meState } = useAuth();
  const lastRequestTime = useRef<number>(0);

  const { data, isFetching, isFetched, dataUpdatedAt, refetch } = useQuery<ICounter, Error>(
    ['counter'],
    async () => {
      const result = await CounterService.getCount();
      if (result.status === 200) return result.data as ICounter;
      throw new Error(result.data as string);
    },
    {
      enabled: !!meState.user.data,
      initialData: initialState,
    },
  );

  const onMouseEnter = useCallback(() => {
    if (isFetching) return;

    // Throttle against both the last hover attempt and the last real response (covers the mount fetch)
    const currentTime = Date.now();
    if (currentTime - Math.max(lastRequestTime.current, dataUpdatedAt) >= 30000) {
      lastRequestTime.current = currentTime;
      refetch();
    }
  }, [isFetching, dataUpdatedAt, refetch]);

  // initialData also sets dataUpdatedAt — only expose the time once a real response has arrived
  const lastUpdatedAt = isFetched ? dataUpdatedAt : null;

  const value = useMemo<CounterContextValue>(() => ({ state: data ?? initialState, onMouseEnter, lastUpdatedAt }), [data, onMouseEnter, lastUpdatedAt]);

  return <CounterContext.Provider value={value}>{children}</CounterContext.Provider>;
};
